import React, { useState, useEffect, useMemo } from 'react';
import { X, Save, Loader2, ShoppingCart, TrendingUp, AlertCircle, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { dashboardApi } from '../api/dashboard';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const VALID_COMMODITIES = ['MIS', 'RAM 27', 'NAGAMAS', 'GENK', 'MIS BR'];
const PAYMENT_STATUSES = [
  { value: 'unpaid', label: 'Belum Dibayar' },
  { value: 'partial', label: 'Sebagian' },
  { value: 'paid', label: 'Lunas' },
];

const fmtRp = (n) => `Rp ${Number(n || 0).toLocaleString('id-ID')}`;

/**
 * Modal for reviewing & editing OCR-parsed data before saving as transaction.
 * Always opens for user review (per user choice). Entity is auto-detected by AI
 * from bon header, but user can override via dropdown.
 */
export default function OcrSaveModal({ ocrData, bonType, onClose, onSaved }) {
  const [entities, setEntities] = useState([]);
  const [loadingEntities, setLoadingEntities] = useState(true);
  const [saving, setSaving] = useState(false);

  // Editable form state
  const [partyName, setPartyName] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [date, setDate] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('unpaid');
  const [paidAmount, setPaidAmount] = useState(0);
  const [items, setItems] = useState([]);
  const [potongan, setPotongan] = useState({ pph22: 0, fee_jalan_per_kg: 0, spsi_per_kg: 0, biaya_transfer: 0, broker_fee: 0 });
  const [entityId, setEntityId] = useState('');
  const [entityDetectedName, setEntityDetectedName] = useState('');
  
  // Cross-entity invoice check (info banner, non-blocking)
  const [crossEntityInfo, setCrossEntityInfo] = useState(null);
  const [checkingInvoice, setCheckingInvoice] = useState(false);

  // Load entities & prefill from ocrData
  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/entities`, { headers: { Authorization: `Bearer ${token}` } });
        setEntities(res.data || []);
      } catch (e) {
        toast.error('Gagal memuat daftar entitas');
      } finally {
        setLoadingEntities(false);
      }
    };
    load();
  }, []);

  // Prefill from ocrData when entities loaded
  useEffect(() => {
    if (!ocrData) return;
    setPartyName(ocrData.supplier_or_buyer || '');
    setInvoiceNumber(ocrData.invoice_number || '');
    setDate(ocrData.date || new Date().toISOString().slice(0, 10));
    setPaymentStatus(ocrData.payment_status || 'unpaid');
    setPaidAmount(Number(ocrData.paid_amount || 0));
    setEntityDetectedName(ocrData.entity_name || '');

    const pot = ocrData.potongan || {};
    setPotongan({
      pph22: Number(pot.pph22 || 0),
      fee_jalan_per_kg: Number(pot.fee_jalan_per_kg || pot.fee_jalan || 0),
      spsi_per_kg: Number(pot.spsi_per_kg || pot.spsi || 0),
      biaya_transfer: Number(pot.biaya_transfer || 0),
      broker_fee: Number(pot.broker_fee || 0),
    });

    const rawItems = ocrData.items || [];
    if (rawItems.length > 0) {
      setItems(rawItems.map((it) => ({
        commodity_type: (it.commodity_type || ocrData.commodity_type || 'MIS').toUpperCase(),
        quantity_kg: Number(it.quantity_kg || 0),
        price_per_kg: Number(it.price_per_kg || ocrData.price_per_kg || 0),
        sp_date: it.sp_date || ocrData.date || '',
      })));
    } else if (ocrData.quantity_kg) {
      setItems([{
        commodity_type: (ocrData.commodity_type || 'MIS').toUpperCase(),
        quantity_kg: Number(ocrData.quantity_kg || 0),
        price_per_kg: Number(ocrData.price_per_kg || 0),
        sp_date: ocrData.date || '',
      }]);
    }
  }, [ocrData]);

  // Auto-detect entity once entities + ocr data are loaded
  useEffect(() => {
    if (!entities.length || entityId) return;
    if (entityDetectedName) {
      const low = entityDetectedName.trim().toLowerCase();
      let match = entities.find((e) => e.name.trim().toLowerCase() === low);
      if (!match) match = entities.find((e) => low.includes(e.name.toLowerCase()) || e.name.toLowerCase().includes(low));
      if (match) {
        setEntityId(match.id);
        return;
      }
    }
    // fallback to active entity in localStorage
    const active = localStorage.getItem('activeEntityId');
    if (active && entities.some((e) => e.id === active)) {
      setEntityId(active);
    } else if (entities[0]) {
      setEntityId(entities[0].id);
    }
  }, [entities, entityDetectedName, entityId]);

  // Cross-entity invoice check (debounced) — info banner only, non-blocking
  useEffect(() => {
    const inv = invoiceNumber.trim();
    if (!inv || !entityId) {
      setCrossEntityInfo(null);
      return undefined;
    }
    setCheckingInvoice(true);
    const timer = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/transactions/check-invoice-cross-entity`, {
          params: {
            invoice_number: inv,
            transaction_type: bonType === 'sale' ? 'sale' : 'purchase',
            current_entity_id: entityId,
          },
          headers: { Authorization: `Bearer ${token}` },
        });
        setCrossEntityInfo(res.data);
      } catch (e) {
        setCrossEntityInfo(null);
      } finally {
        setCheckingInvoice(false);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [invoiceNumber, entityId, bonType]);

  const updateItem = (idx, field, value) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  };

  const addItem = () => {
    setItems((prev) => [...prev, { commodity_type: 'MIS', quantity_kg: 0, price_per_kg: 0, sp_date: date }]);
  };

  const removeItem = (idx) => {
    if (items.length === 1) {
      toast.error('Minimal 1 item');
      return;
    }
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  // Calculate total preview
  const totalPreview = useMemo(() => {
    const subtotal = items.reduce((s, it) => s + Number(it.quantity_kg || 0) * Number(it.price_per_kg || 0), 0);
    const totalQty = items.reduce((s, it) => s + Number(it.quantity_kg || 0), 0);
    const feeJalanTotal = Number(potongan.fee_jalan_per_kg || 0) * totalQty;
    const spsiTotal = Number(potongan.spsi_per_kg || 0) * totalQty;
    const pph = Number(potongan.pph22 || 0);
    const biayaTransfer = bonType === 'sale' ? Number(potongan.biaya_transfer || 0) : 0;
    const brokerFee = bonType === 'purchase' ? Number(potongan.broker_fee || 0) : 0;
    const net = subtotal - pph - feeJalanTotal - spsiTotal - biayaTransfer + brokerFee;
    return { subtotal, feeJalanTotal, spsiTotal, biayaTransfer, brokerFee, pph, net };
  }, [items, potongan, bonType]);

  const handleSave = async () => {
    if (!partyName.trim()) {
      toast.error(bonType === 'purchase' ? 'Nama supplier wajib diisi' : 'Nama buyer wajib diisi');
      return;
    }
    if (!entityId) {
      toast.error('Pilih entitas tujuan');
      return;
    }
    if (items.length === 0 || items.some((it) => !Number(it.quantity_kg) || !Number(it.price_per_kg))) {
      toast.error('Setiap item wajib memiliki quantity dan harga');
      return;
    }
    for (const it of items) {
      if (!VALID_COMMODITIES.includes(it.commodity_type)) {
        toast.error(`Tempat penjualan "${it.commodity_type}" tidak valid. Pilih: ${VALID_COMMODITIES.join(', ')}`);
        return;
      }
    }

    const payload = {
      bon_type: bonType,
      entity_name: entityDetectedName,
      supplier_or_buyer: partyName.trim(),
      invoice_number: invoiceNumber.trim() || null,
      date,
      payment_status: paymentStatus,
      paid_amount: paymentStatus === 'partial' ? Number(paidAmount) : 0,
      items: items.map((it) => ({
        commodity_type: it.commodity_type,
        quantity_kg: Number(it.quantity_kg),
        price_per_kg: Number(it.price_per_kg),
        sp_date: it.sp_date || date,
      })),
      potongan: { ...potongan },
    };

    setSaving(true);
    try {
      const res = await dashboardApi.saveOcrTransaction(payload, entityId);
      toast.success(res.message || 'Transaksi tersimpan');
      onSaved?.(res);
      onClose?.();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Gagal menyimpan transaksi';
      toast.error(typeof msg === 'string' ? msg : 'Gagal menyimpan transaksi');
    } finally {
      setSaving(false);
    }
  };

  const isPurchase = bonType === 'purchase';
  // Pre-built class maps so Tailwind JIT can statically detect classes
  const accent = isPurchase
    ? {
        headerBg: 'bg-emerald-50',
        icon: 'text-emerald-600',
        summaryBg: 'bg-gradient-to-br from-emerald-50 to-emerald-100/60',
        summaryBorder: 'border-emerald-200',
        summaryDivider: 'border-emerald-300',
        summaryText: 'text-emerald-700',
        btn: 'bg-emerald-600 hover:bg-emerald-700',
      }
    : {
        headerBg: 'bg-blue-50',
        icon: 'text-blue-600',
        summaryBg: 'bg-gradient-to-br from-blue-50 to-blue-100/60',
        summaryBorder: 'border-blue-200',
        summaryDivider: 'border-blue-300',
        summaryText: 'text-blue-700',
        btn: 'bg-blue-600 hover:bg-blue-700',
      };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4" data-testid="ocr-save-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-4 border-b border-stone-200 ${accent.headerBg} rounded-t-2xl`}>
          <div className="flex items-center gap-2">
            {isPurchase ? <ShoppingCart className={accent.icon} size={20} /> : <TrendingUp className={accent.icon} size={20} />}
            <h2 className="font-bold text-slate-800 text-lg">
              Tinjau & Simpan {isPurchase ? 'Pembelian' : 'Penjualan'} (Hasil OCR)
            </h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="ocr-modal-close">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Confidence banner */}
          {ocrData?.confidence && ocrData.confidence !== 'high' && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
              <div>
                Confidence OCR: <b>{ocrData.confidence}</b>. Pastikan data sudah benar sebelum menyimpan.
              </div>
            </div>
          )}

          {/* Entity selection */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              Entitas Tujuan {entityDetectedName && <span className="text-emerald-600 font-normal">(AI deteksi: {entityDetectedName})</span>}
            </label>
            <select
              value={entityId}
              onChange={(e) => setEntityId(e.target.value)}
              disabled={loadingEntities}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              data-testid="ocr-entity-select"
            >
              {loadingEntities && <option>Memuat...</option>}
              {entities.map((e) => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          </div>

          {/* Party + invoice */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">
                {isPurchase ? 'Supplier' : 'Buyer / Pabrik'}
              </label>
              <input
                type="text"
                value={partyName}
                onChange={(e) => setPartyName(e.target.value)}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                data-testid="ocr-party-input"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">No. Invoice/Bon</label>
              <input
                type="text"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                placeholder="(otomatis jika kosong)"
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                data-testid="ocr-invoice-input"
              />
              {/* Cross-entity invoice info banner */}
              {invoiceNumber.trim() && crossEntityInfo && crossEntityInfo.exists_in_other_entities.length > 0 && (
                <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-[11px]" data-testid="cross-entity-invoice-info">
                  <div className="flex items-start gap-2">
                    <AlertCircle size={13} className="text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-blue-900 font-medium">
                        Invoice <span className="font-mono">{crossEntityInfo.invoice_number}</span> juga ada di entity lain
                      </p>
                      {crossEntityInfo.exists_in_other_entities.map((ent) => (
                        <p key={ent.entity_id} className="text-blue-700 mt-0.5">
                          • <span className="font-medium">{ent.entity_name}</span> ({ent.count} record)
                        </p>
                      ))}
                      <p className="text-blue-600/80 mt-1 italic">
                        OK jika ini split invoice lintas entity (1 SP dipecah). Lanjut simpan saja.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {checkingInvoice && invoiceNumber.trim() && !crossEntityInfo && (
                <p className="mt-1 text-[10px] text-stone-400">Mengecek invoice lintas entity...</p>
              )}
            </div>
          </div>

          {/* Date + payment */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Tanggal</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                data-testid="ocr-date-input"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Status Bayar</label>
              <select
                value={paymentStatus}
                onChange={(e) => setPaymentStatus(e.target.value)}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                data-testid="ocr-payment-status"
              >
                {PAYMENT_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            {paymentStatus === 'partial' && (
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">DP Dibayar (Rp)</label>
                <input
                  type="number"
                  value={paidAmount}
                  onChange={(e) => setPaidAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  data-testid="ocr-paid-amount"
                />
              </div>
            )}
          </div>

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-slate-600">Item Transaksi ({items.length})</label>
              <button onClick={addItem} className="flex items-center gap-1 px-2 py-1 text-xs bg-stone-100 hover:bg-stone-200 rounded text-slate-700" data-testid="ocr-add-item">
                <Plus size={12} /> Tambah Item
              </button>
            </div>
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-stone-50 p-2 rounded-lg" data-testid={`ocr-item-${idx}`}>
                  <select
                    value={it.commodity_type}
                    onChange={(e) => updateItem(idx, 'commodity_type', e.target.value)}
                    className="col-span-3 px-2 py-1.5 border border-stone-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                  >
                    {VALID_COMMODITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <input
                    type="number"
                    value={it.quantity_kg}
                    onChange={(e) => updateItem(idx, 'quantity_kg', e.target.value)}
                    placeholder="Kg"
                    className="col-span-3 px-2 py-1.5 border border-stone-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                  />
                  <input
                    type="number"
                    value={it.price_per_kg}
                    onChange={(e) => updateItem(idx, 'price_per_kg', e.target.value)}
                    placeholder="Rp/kg"
                    className="col-span-3 px-2 py-1.5 border border-stone-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                  />
                  <div className="col-span-2 text-right text-xs text-slate-600">
                    {fmtRp(Number(it.quantity_kg || 0) * Number(it.price_per_kg || 0))}
                  </div>
                  <button
                    onClick={() => removeItem(idx)}
                    className="col-span-1 text-red-500 hover:text-red-700 p-1 disabled:opacity-30"
                    disabled={items.length === 1}
                    data-testid={`ocr-remove-item-${idx}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Potongan */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-2">Potongan / Biaya Tambahan</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              <div>
                <span className="text-[10px] text-slate-500">PPh 22 (Rp)</span>
                <input type="number" value={potongan.pph22} onChange={(e) => setPotongan({ ...potongan, pph22: e.target.value })} className="w-full px-2 py-1.5 border border-stone-300 rounded text-xs" />
              </div>
              <div>
                <span className="text-[10px] text-slate-500">Fee Jalan (Rp/kg)</span>
                <input type="number" value={potongan.fee_jalan_per_kg} onChange={(e) => setPotongan({ ...potongan, fee_jalan_per_kg: e.target.value })} className="w-full px-2 py-1.5 border border-stone-300 rounded text-xs" />
              </div>
              <div>
                <span className="text-[10px] text-slate-500">SPSI (Rp/kg)</span>
                <input type="number" value={potongan.spsi_per_kg} onChange={(e) => setPotongan({ ...potongan, spsi_per_kg: e.target.value })} className="w-full px-2 py-1.5 border border-stone-300 rounded text-xs" />
              </div>
              {bonType === 'sale' && (
                <div>
                  <span className="text-[10px] text-slate-500">Biaya Transfer (Rp)</span>
                  <input type="number" value={potongan.biaya_transfer} onChange={(e) => setPotongan({ ...potongan, biaya_transfer: e.target.value })} className="w-full px-2 py-1.5 border border-stone-300 rounded text-xs" />
                </div>
              )}
              {bonType === 'purchase' && (
                <div>
                  <span className="text-[10px] text-slate-500">Broker Fee (Rp)</span>
                  <input type="number" value={potongan.broker_fee} onChange={(e) => setPotongan({ ...potongan, broker_fee: e.target.value })} className="w-full px-2 py-1.5 border border-stone-300 rounded text-xs" />
                </div>
              )}
            </div>
          </div>

          {/* Summary */}
          <div className={`p-3 rounded-lg ${accent.summaryBg} border ${accent.summaryBorder}`}>
            <div className="text-xs text-slate-600 space-y-0.5">
              <div className="flex justify-between"><span>Subtotal</span><span>{fmtRp(totalPreview.subtotal)}</span></div>
              {totalPreview.pph > 0 && <div className="flex justify-between"><span>PPh 22</span><span>- {fmtRp(totalPreview.pph)}</span></div>}
              {totalPreview.feeJalanTotal > 0 && <div className="flex justify-between"><span>Fee Jalan</span><span>- {fmtRp(totalPreview.feeJalanTotal)}</span></div>}
              {totalPreview.spsiTotal > 0 && <div className="flex justify-between"><span>SPSI</span><span>- {fmtRp(totalPreview.spsiTotal)}</span></div>}
              {totalPreview.biayaTransfer > 0 && <div className="flex justify-between"><span>Biaya Transfer</span><span>- {fmtRp(totalPreview.biayaTransfer)}</span></div>}
              {totalPreview.brokerFee > 0 && <div className="flex justify-between"><span>Broker Fee</span><span>+ {fmtRp(totalPreview.brokerFee)}</span></div>}
            </div>
            <div className={`flex justify-between items-center mt-2 pt-2 border-t ${accent.summaryDivider} font-bold ${accent.summaryText}`}>
              <span>Total {isPurchase ? 'Pembelian' : 'Penjualan'}</span>
              <span className="text-lg" data-testid="ocr-total-preview">{fmtRp(totalPreview.net)}</span>
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="flex items-center gap-2 px-5 py-4 border-t border-stone-200 bg-stone-50 rounded-b-2xl">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-stone-300 rounded-lg hover:bg-stone-100 disabled:opacity-50"
            data-testid="ocr-cancel-btn"
          >
            Batal
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !entityId}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 ${accent.btn} text-white rounded-lg text-sm font-semibold disabled:opacity-50`}
            data-testid="ocr-save-btn"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {saving ? 'Menyimpan...' : `Simpan ${isPurchase ? 'Pembelian' : 'Penjualan'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
